import { computed, ref } from 'vue';
import { ElMessage } from 'element-plus';
import { ANY_OBJECT } from '@/types/generic';

const isUploading = ref(false);
const isCompressing = ref(false);
const isWaitingServer = ref(false);
const isUploadComplete = ref(false);
const uploadProgress = ref(0);
const compressProgress = ref(0);
const compressPhase = ref<'compressing' | 'encoding'>('compressing');
const uploadingFileName = ref('');
let resetTimer: ReturnType<typeof window.setTimeout> | null = null;

const clearResetTimer = () => {
  if (resetTimer != null) {
    window.clearTimeout(resetTimer);
    resetTimer = null;
  }
};

export const resetUploadProgress = () => {
  clearResetTimer();
  isUploading.value = false;
  isCompressing.value = false;
  isWaitingServer.value = false;
  isUploadComplete.value = false;
  uploadProgress.value = 0;
  compressProgress.value = 0;
  compressPhase.value = 'compressing';
  uploadingFileName.value = '';
};

export const startVideoCompress = (fileName?: string) => {
  clearResetTimer();
  isUploading.value = true;
  isCompressing.value = true;
  isWaitingServer.value = false;
  isUploadComplete.value = false;
  uploadProgress.value = 0;
  compressProgress.value = 0;
  compressPhase.value = 'compressing';
  uploadingFileName.value = fileName || '视频';
};

export const onVideoCompressPhaseChange = (phase: 'compressing' | 'encoding') => {
  compressPhase.value = phase;
};

export const onVideoCompressProgress = (percent: number) => {
  compressProgress.value = Math.min(Math.round(percent), 99);
};

export const finishVideoCompress = (fileName?: string) => {
  isCompressing.value = false;
  compressProgress.value = 100;
  compressPhase.value = 'compressing';
  uploadProgress.value = 0;
  if (fileName) {
    uploadingFileName.value = fileName;
  }
};

export const startUploadProgress = (fileName?: string) => {
  clearResetTimer();
  isUploading.value = true;
  isWaitingServer.value = false;
  isUploadComplete.value = false;
  uploadProgress.value = 0;
  uploadingFileName.value = fileName || '文件';
};

export const onUploadProgressEvent = (evt?: { percent?: number }) => {
  const percent = evt?.percent ?? 0;
  if (percent >= 100) {
    isWaitingServer.value = true;
    uploadProgress.value = 99;
    return;
  }
  isWaitingServer.value = false;
  uploadProgress.value = Math.min(percent, 99);
};

export const onAxiosUploadProgress = (progressEvent: { loaded: number; total?: number }) => {
  if (!progressEvent.total) {
    return;
  }
  onUploadProgressEvent({
    percent: (progressEvent.loaded / progressEvent.total) * 100,
  });
};

export const completeUploadProgressSuccess = (autoResetMs = 1000) => {
  isUploadComplete.value = true;
  isWaitingServer.value = false;
  uploadProgress.value = 100;
  if (autoResetMs > 0) {
    resetTimer = window.setTimeout(() => {
      resetUploadProgress();
    }, autoResetMs);
  }
};

export const completeUploadProgressError = (message?: string) => {
  resetUploadProgress();
  if (message) {
    ElMessage.error(message);
  }
};

export const getUploadFileNameFromParams = (params: ANY_OBJECT) => {
  for (const key of Object.keys(params)) {
    const value = params[key];
    if (value instanceof File) {
      return value.name;
    }
    if (value && typeof value === 'object' && 'name' in value && 'size' in value) {
      return String((value as File).name);
    }
  }
  return '文件';
};

export function useUploadProgress() {
  const displayUploadProgress = computed(() => {
    if (isUploadComplete.value) {
      return 100;
    }
    if (isCompressing.value) {
      return Math.min(Math.round(compressProgress.value), 99);
    }
    return Math.min(Math.round(uploadProgress.value), 99);
  });

  const uploadStatusText = computed(() => {
    if (isCompressing.value) {
      if (compressPhase.value === 'encoding') {
        return `正在完成「${uploadingFileName.value}」编码，请稍候...`;
      }
      return `正在压缩「${uploadingFileName.value}」（${displayUploadProgress.value}%），请稍候...`;
    }
    if (isUploadComplete.value) {
      return `「${uploadingFileName.value}」上传成功`;
    }
    if (!uploadingFileName.value) {
      return isWaitingServer.value
        ? '文件已发送，正在等待服务器处理，请耐心等待...'
        : '正在上传文件，请勿关闭页面...';
    }
    if (isWaitingServer.value) {
      return `「${uploadingFileName.value}」已发送，正在等待服务器处理，请耐心等待...`;
    }
    return `正在上传「${uploadingFileName.value}」（${displayUploadProgress.value}%），请勿关闭页面...`;
  });

  return {
    isUploading,
    isCompressing,
    isUploadComplete,
    displayUploadProgress,
    uploadStatusText,
    startUploadProgress,
    startVideoCompress,
    onVideoCompressProgress,
    onVideoCompressPhaseChange,
    finishVideoCompress,
    onUploadProgressEvent,
    onAxiosUploadProgress,
    completeUploadProgressSuccess,
    completeUploadProgressError,
    resetUploadProgress,
  };
}
